import type { AxiosResponse } from 'axios';
import axios from 'axios';

import type { SiteId, Channel, Timestamp, Url } from './common';
import { createSiteEndpointPath } from './common';

export type WidgetId = number;

type ConfigurationId = number;

export type WidgetType = 'incoming_feedback' | 'poll' | 'recording_segment';

export interface Configuration {
  /* eslint-disable camelcase */
  id: ConfigurationId;
  site_id: SiteId;
  widget_id: WidgetId;
  widget_type: WidgetType;
  created_epoch_time: Timestamp;
  enabled: boolean;
  channels: Array<Channel>;
  /* eslint-enable camelcase */
}

type PollQuestionId = string;

interface PollSettings {
  questions: Array<PollQuestionId>;
}

interface PollConfiguration extends Configuration {
  settings: PollSettings;
}

interface Payload {
  enabled: boolean;
  channels: Array<Channel>;
}

interface PollPayload extends Payload {
  settings: PollSettings;
}

function createEndpointPath(siteId: SiteId, widgetType: WidgetType, widgetId: WidgetId): Url {
  return `${createSiteEndpointPath(siteId)}/widgets/${widgetType}/${widgetId}`;
}

export function get(
  siteId: SiteId,
  widgetType: 'poll',
  widgetId: WidgetId,
): Promise<AxiosResponse<PollConfiguration>>;

export function get(
  siteId: SiteId,
  widgetType: WidgetType,
  widgetId: WidgetId,
): Promise<AxiosResponse<Configuration>>;

export function get(
  siteId: SiteId,
  widgetType: WidgetType,
  widgetId: WidgetId,
): Promise<AxiosResponse<Configuration | PollConfiguration>> {
  const url = createEndpointPath(siteId, widgetType, widgetId);
  return axios.get(url);
}

export function set(
  siteId: SiteId,
  widgetType: 'incoming_feedback' | 'recording_segment',
  widgetId: WidgetId,
  payload: Payload,
): Promise<AxiosResponse<Configuration>>;

export function set(
  siteId: SiteId,
  widgetType: 'poll',
  widgetId: WidgetId,
  payload: PollPayload,
): Promise<AxiosResponse<PollConfiguration>>;

// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
export function set(
  siteId: SiteId,
  widgetType: WidgetType,
  widgetId: WidgetId,
  payload: Payload | PollPayload,
): Promise<AxiosResponse<Configuration | PollConfiguration>> {
  const url = createEndpointPath(siteId, widgetType, widgetId);
  return axios.post(url, payload);
}

export function update(
  siteId: SiteId,
  widgetType: 'incoming_feedback' | 'recording_segment',
  widgetId: WidgetId,
  payload: Payload,
): Promise<AxiosResponse<Configuration>>;

export function update(
  siteId: SiteId,
  widgetType: 'poll',
  widgetId: WidgetId,
  payload: PollPayload,
): Promise<AxiosResponse<PollConfiguration>>;

// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
export function update(
  siteId: SiteId,
  widgetType: WidgetType,
  widgetId: WidgetId,
  payload: Payload,
): Promise<AxiosResponse<Configuration | PollConfiguration>> {
  const url = createEndpointPath(siteId, widgetType, widgetId);
  return axios.put(url, payload);
}
